import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem,
  Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { AccountService } from '../../services/account.service';
import { SegmentService } from '../../services/segment.service';
import { TxnMappingRuleService, TxnMappingRuleRequest } from '../../services/txnMappingRules.service';
import { PERMISSIONS } from '../../constants/permissions';
import { Account, Segment } from '../../interfaces/accounting.interface';
import {
  BankRule, BankRuleCondition, MATCH_MODE_LABEL, OPERATOR_LABEL, RuleField, RuleKindInfo, RuleMatchMode, RuleOperator,
} from '../../interfaces/bank/bankRule.interface';
import { CashSide } from '../../interfaces/cashbook/cashbook.interface';
import { SearchableSelect } from '../common/SearchableSelect';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { formatDate } from '../../utils/dateUtils';
import { leafAccounts } from '../../utils/postingAccounts';

/** Account types a category of each side may be (mirrors TxnMappingRuleKind.categoryTypes). */
const CATEGORY_TYPES: Record<CashSide, string[]> = {
  INCOME: ['REVENUE', 'LIABILITY', 'EQUITY'],
  EXPENSE: ['EXPENSE', 'ASSET'],
};
const SIDE_LABEL: Record<CashSide, string> = { INCOME: 'Cash in', EXPENSE: 'Cash out' };

interface ConditionRow { field: string; operator: RuleOperator | ''; valueText: string; valueNumber: string; valueNumberTo: string; }
const emptyCondition = (field = 'DESCRIPTION'): ConditionRow => ({ field, operator: '', valueText: '', valueNumber: '', valueNumberTo: '' });
const toRow = (c: BankRuleCondition): ConditionRow => ({
  field: c.field, operator: c.operator, valueText: c.valueText ?? '',
  valueNumber: c.valueNumber != null ? String(c.valueNumber) : '',
  valueNumberTo: c.valueNumberTo != null ? String(c.valueNumberTo) : '',
});

interface EditorProps {
  side: CashSide;
  rule: BankRule | null;
  kind: RuleKindInfo | null;
  accounts: Account[];
  segments: Segment[];
  onClose: () => void;
  onSaved: () => void;
}

/** Create / edit one transaction mapping rule: conditions on the cash form, then the category (and segment) it sets. */
const TxnMappingRuleEditor: React.FC<EditorProps> = ({ side, rule, kind, accounts, segments, onClose, onSaved }) => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const [name, setName] = useState(rule?.name ?? '');
  const [matchMode, setMatchMode] = useState<RuleMatchMode>(rule?.matchMode ?? 'ALL');
  const [conditions, setConditions] = useState<ConditionRow[]>(() => rule?.conditions.length ? rule.conditions.map(toRow) : [emptyCondition()]);
  const [accountId, setAccountId] = useState(rule?.action?.accountId ?? '');
  const [segmentId, setSegmentId] = useState(rule?.action?.segmentId ?? '');
  const [saving, setSaving] = useState(false);

  const fieldOf = (code: string): RuleField | undefined => kind?.fields.find(f => f.code === code);
  const operatorsFor = (code: string): RuleOperator[] => {
    const f = fieldOf(code);
    return f && kind ? kind.operators[f.type] ?? [] : [];
  };
  const setCondition = (i: number, patch: Partial<ConditionRow>) =>
    setConditions(rows => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const changeField = (i: number, field: string) =>
    setCondition(i, { field, operator: operatorsFor(field)[0] ?? '', valueText: '', valueNumber: '', valueNumberTo: '' });

  useEffect(() => {
    if (!kind) return;
    setConditions(rows => rows.map(r => (r.operator || !r.field ? r : { ...r, operator: operatorsFor(r.field)[0] ?? '' })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const posting = useMemo(() => leafAccounts(accounts), [accounts]);
  const categoryOptions = useMemo(() => posting.filter(a => CATEGORY_TYPES[side].includes(a.accountType)), [posting, side]);
  const cashOptions = useMemo(() => posting.filter(a => a.accountType === 'ASSET'), [posting]);

  const conditionsFilled = conditions.length > 0 && conditions.every(r => {
    const f = fieldOf(r.field);
    if (!f || !r.operator) return false;
    if (f.type === 'NUMBER') return r.valueNumber !== '' && (r.operator !== 'BETWEEN' || r.valueNumberTo !== '');
    return r.valueText.trim() !== '';
  });
  const valid = name.trim() !== '' && !!accountId && conditionsFilled;

  const buildRequest = (): TxnMappingRuleRequest => ({
    subjectType: side,
    name: name.trim(),
    scopeId: null,
    matchMode,
    conditions: conditions.map(r => {
      const isNumber = fieldOf(r.field)?.type === 'NUMBER';
      const op = r.operator as RuleOperator;
      return {
        field: r.field,
        operator: op,
        valueText: isNumber ? null : r.valueText.trim(),
        valueNumber: isNumber && r.valueNumber !== '' ? Number(r.valueNumber) : null,
        valueNumberTo: isNumber && op === 'BETWEEN' && r.valueNumberTo !== '' ? Number(r.valueNumberTo) : null,
      };
    }),
    action: { actionType: 'MAP_TXN', accountId: accountId || null, segmentId: segmentId || null },
  });

  const handleSave = async () => {
    try {
      setSaving(true);
      if (rule) await TxnMappingRuleService.update(institutionId, rule.id, buildRequest());
      else await TxnMappingRuleService.create(institutionId, buildRequest());
      toast.showSuccess(rule ? 'Rule updated' : 'Rule created');
      onSaved();
    } catch (e) {
      toast.showApiError(e, 'Failed to save the rule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog data-help-page="accounting.txnRules.form.page" open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{rule ? 'Edit mapping rule' : `New ${SIDE_LABEL[side].toLowerCase()} mapping rule`}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pt: 1 }}>
          <TextField data-help="accounting.txnRules.form.field.name" label="Rule name" required size="small" value={name} onChange={e => setName(e.target.value)}
            inputProps={{ maxLength: 120 }} sx={{ flex: '2 1 260px', maxWidth: 420 }} />
          <TextField data-help="accounting.txnRules.form.field.match" select label="Match" size="small" value={matchMode} onChange={e => setMatchMode(e.target.value as RuleMatchMode)}
            sx={{ flex: '1 1 220px', maxWidth: 300 }}>
            {(Object.keys(MATCH_MODE_LABEL) as RuleMatchMode[]).map(m => <MenuItem key={m} value={m}>{MATCH_MODE_LABEL[m]}</MenuItem>)}
          </TextField>
        </Box>

        <Typography variant="subtitle2">Conditions</Typography>
        {conditions.map((row, i) => {
          const f = fieldOf(row.field);
          return (
            <Box key={i} data-testid={`txn-rule-condition-${i}`} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              <TextField data-help="accounting.txnRules.form.cond.field" select size="small" label="Field" value={row.field} onChange={e => changeField(i, e.target.value)}
                sx={{ flex: '1 1 160px', maxWidth: 220 }}>
                {(kind?.fields ?? []).map(fl => <MenuItem key={fl.code} value={fl.code}>{fl.label}</MenuItem>)}
              </TextField>
              <TextField data-help="accounting.txnRules.form.cond.operator" select size="small" label="Operator" value={row.operator}
                onChange={e => setCondition(i, { operator: e.target.value as RuleOperator })} sx={{ flex: '1 1 150px', maxWidth: 200 }}>
                {operatorsFor(row.field).map(o => <MenuItem key={o} value={o}>{OPERATOR_LABEL[o]}</MenuItem>)}
              </TextField>
              {f?.type === 'REFERENCE' ? (
                <Box sx={{ flex: '2 1 240px', maxWidth: 380 }}>
                  <AccountAutocomplete data-help="accounting.txnRules.form.cond.cash" accounts={cashOptions} value={row.valueText} size="small"
                    onChange={id => setCondition(i, { valueText: id })} label="Cash / bank account" />
                </Box>
              ) : f?.type === 'NUMBER' ? (
                <>
                  <TextField data-help="accounting.txnRules.form.cond.amount" size="small" type="number" label={row.operator === 'BETWEEN' ? 'From' : 'Amount'} value={row.valueNumber}
                    inputProps={{ min: 0 }} onChange={e => setCondition(i, { valueNumber: e.target.value })} sx={{ flex: '1 1 130px', maxWidth: 200 }} />
                  {row.operator === 'BETWEEN' && (
                    <TextField data-help="accounting.txnRules.form.cond.amountTo" size="small" type="number" label="To" value={row.valueNumberTo} inputProps={{ min: 0 }}
                      onChange={e => setCondition(i, { valueNumberTo: e.target.value })} sx={{ flex: '1 1 130px', maxWidth: 200 }} />
                  )}
                </>
              ) : (
                <TextField data-help="accounting.txnRules.form.cond.text" size="small" label="Text" value={row.valueText} onChange={e => setCondition(i, { valueText: e.target.value })}
                  inputProps={{ maxLength: 255 }} sx={{ flex: '2 1 200px', maxWidth: 360 }} />
              )}
              <IconButton data-help="accounting.txnRules.action.removeCondition" size="small" aria-label="Remove condition" disabled={conditions.length <= 1}
                onClick={() => setConditions(rows => rows.filter((_, idx) => idx !== i))}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
        <Box>
          <Button data-help="accounting.txnRules.form.addCondition" size="small" startIcon={<AddIcon />} disabled={conditions.length >= (kind?.maxConditions ?? 10)}
            onClick={() => setConditions(rows => [...rows, emptyCondition()])}>Add condition</Button>
        </Box>

        <Typography variant="subtitle2">Then set</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: '2 1 280px', maxWidth: 440 }}>
            <AccountAutocomplete data-help="accounting.txnRules.form.field.category" accounts={categoryOptions} value={accountId} onChange={setAccountId} size="small" required
              label={side === 'INCOME' ? 'Category (income, liability or equity)' : 'Category (expense or asset)'} />
          </Box>
          <Box sx={{ flex: '1 1 200px', maxWidth: 300 }}>
            <SearchableSelect<Segment>
              data-help="accounting.txnRules.form.field.segment"
              options={segments} value={segmentId} onChange={id => setSegmentId(id)} size="small"
              getOptionLabel={s => `${s.code} - ${s.name}`} label="Segment" placeholder="(keep the form's)" data-testid="txn-rule-segment" />
          </Box>
        </Box>
        {!valid && <Alert severity="info" sx={{ py: 0 }}>Give the rule a name, at least one complete condition and a category account.</Alert>}
      </DialogContent>
      <DialogActions>
        <Button data-help="accounting.txnRules.form.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="accounting.txnRules.form.save" variant="contained" disabled={saving || !valid} onClick={handleSave}>Save rule</Button>
      </DialogActions>
    </Dialog>
  );
};

/** Accounting > Transaction mapping rules (F11 11c): when a cash transaction is recorded or imported with the category
 *  left on "Auto", the first matching rule of its side (top to bottom) sets the category account and segment. */
const TxnMappingRules: React.FC = () => {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.BANK_RULE_MANAGE);
  const institutionId = AuthService.getSelectedInstitution()!;
  const [side, setSide] = useState<CashSide>('EXPENSE');
  const [rules, setRules] = useState<BankRule[]>([]);
  const [kind, setKind] = useState<RuleKindInfo | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState<{ rule: BankRule | null } | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const list = await TxnMappingRuleService.list(institutionId, side, {
        active: status === '' ? undefined : status === 'ACTIVE',
        search: search.trim() || undefined,
      });
      setRules(Array.isArray(list) ? list : []);
    } catch (e) {
      toast.showApiError(e, 'Failed to load mapping rules');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId, side, status, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.resolve(TxnMappingRuleService.fields(side)).then(k => setKind(k ?? null))
      .catch((e: unknown) => toast.showApiError(e, 'Failed to load rule fields'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [side]);
  useEffect(() => {
    Promise.resolve(AccountService.getAllAccounts()).then((a: Account[]) => setAccounts(Array.isArray(a) ? a : []))
      .catch(() => setAccounts([]));
    Promise.resolve(SegmentService.getByInstitution())
      .then(res => setSegments((res?.content ?? []).filter((s: Segment) => s.active !== false)))
      .catch(() => setSegments([]));
  }, []);

  const accountLabel = (id?: string | null) => {
    const a = accounts.find(x => x.id === id);
    return a ? `${a.code} - ${a.name}` : '—';
  };
  const describe = (c: BankRuleCondition): string => {
    const f = kind?.fields.find(x => x.code === c.field);
    const label = f?.label ?? c.field;
    const op = OPERATOR_LABEL[c.operator];
    if (f?.type === 'REFERENCE') return `${label} ${op} ${accountLabel(c.valueText)}`;
    if (c.valueNumber != null) {
      return c.operator === 'BETWEEN' ? `${label} ${op} ${c.valueNumber} and ${c.valueNumberTo}` : `${label} ${op} ${c.valueNumber}`;
    }
    return `${label} ${op} “${c.valueText ?? ''}”`;
  };

  const unfiltered = !status && !search.trim();
  const move = async (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= rules.length) return;
    const next = [...rules];
    [next[index], next[target]] = [next[target], next[index]];
    try {
      setRules(await TxnMappingRuleService.reorder(institutionId, side, next.map(r => r.id)));
    } catch (e) {
      toast.showApiError(e, 'Failed to reorder rules');
    }
  };
  const toggle = async (rule: BankRule) => {
    try {
      await TxnMappingRuleService.setActive(institutionId, rule.id, !rule.active);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to change the rule status');
    }
  };
  const remove = async (rule: BankRule) => {
    if (!window.confirm(`Delete rule “${rule.name}”?`)) return;
    try {
      await TxnMappingRuleService.remove(institutionId, rule.id);
      toast.showSuccess('Rule deleted');
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to delete the rule');
    }
  };

  return (
    <Box data-help-page="accounting.txnRules.page" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5">Transaction mapping rules</Typography>
          <Typography variant="body2" color="text.secondary">
            When a cash transaction is recorded or imported with its category left on Auto, the first matching rule (top to bottom) sets the category.
          </Typography>
        </Box>
        {canManage && (
          <Button data-help="accounting.txnRules.new" variant="contained" startIcon={<AddIcon />} onClick={() => setEditor({ rule: null })}>New rule</Button>
        )}
      </Box>

      <Box data-help-section="accounting.txnRules.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <ToggleButtonGroup data-help="accounting.txnRules.side" size="small" exclusive value={side} onChange={(_, v) => v && setSide(v as CashSide)} aria-label="Side">
          <ToggleButton value="EXPENSE">{SIDE_LABEL.EXPENSE}</ToggleButton>
          <ToggleButton value="INCOME">{SIDE_LABEL.INCOME}</ToggleButton>
        </ToggleButtonGroup>
        <TextField data-help="accounting.txnRules.status" select size="small" label="Status" value={status} onChange={e => setStatus(e.target.value)}
          sx={{ flex: '1 1 160px', maxWidth: 220 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="ACTIVE">Active</MenuItem>
          <MenuItem value="INACTIVE">Inactive</MenuItem>
        </TextField>
        <TextField data-help="accounting.txnRules.search" size="small" label="Search" value={search} onChange={e => setSearch(e.target.value)}
          sx={{ flex: '1 1 220px', maxWidth: 320 }} />
      </Box>

      {loading ? <Box sx={{ p: 3 }}><CircularProgress /></Box> : (
        <Paper data-help-section="accounting.txnRules.table" variant="outlined">
          <TableContainer>
            <Table size="small" data-testid="txn-rules-table">
              <TableHead>
                <TableRow>
                  <TableCell data-help="accounting.txnRules.col.order">#</TableCell>
                  <TableCell data-help="accounting.txnRules.col.rule">Rule</TableCell>
                  <TableCell data-help="accounting.txnRules.col.when">When</TableCell>
                  <TableCell data-help="accounting.txnRules.col.category">Sets category</TableCell>
                  <TableCell data-help="accounting.txnRules.col.used" align="right">Used</TableCell>
                  <TableCell data-help="accounting.txnRules.col.lastUsed">Last used</TableCell>
                  <TableCell data-help="accounting.txnRules.col.active">Active</TableCell>
                  {canManage && <TableCell data-help="accounting.txnRules.col.actions" align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((r, i) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                    <TableCell sx={{ whiteSpace: 'normal', minWidth: 220 }}>
                      {r.conditions.map((c, idx) => <Chip key={idx} size="small" variant="outlined" label={describe(c)} sx={{ mr: 0.5, mb: 0.5 }} />)}
                      {r.conditions.length > 1 && <Typography variant="caption" color="text.secondary">{MATCH_MODE_LABEL[r.matchMode]}</Typography>}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'normal' }}>{accountLabel(r.action?.accountId)}</TableCell>
                    <TableCell align="right">{r.hitCount}</TableCell>
                    <TableCell>{r.lastHitAt ? formatDate(r.lastHitAt) : '—'}</TableCell>
                    <TableCell>
                      <Switch data-help="accounting.txnRules.action.toggle" size="small" checked={r.active} disabled={!canManage} onChange={() => toggle(r)}
                        inputProps={{ 'aria-label': `${r.active ? 'Deactivate' : 'Activate'} ${r.name}` }} />
                    </TableCell>
                    {canManage && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={unfiltered ? 'Move up' : 'Clear the filters to reorder'}>
                          <span><IconButton data-help="accounting.txnRules.action.up" size="small" disabled={!unfiltered || i === 0} onClick={() => move(i, -1)} aria-label={`Move ${r.name} up`}><ArrowUpwardIcon fontSize="small" /></IconButton></span>
                        </Tooltip>
                        <Tooltip title={unfiltered ? 'Move down' : 'Clear the filters to reorder'}>
                          <span><IconButton data-help="accounting.txnRules.action.down" size="small" disabled={!unfiltered || i === rules.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${r.name} down`}><ArrowDownwardIcon fontSize="small" /></IconButton></span>
                        </Tooltip>
                        <IconButton data-help="accounting.txnRules.action.edit" size="small" onClick={() => setEditor({ rule: r })} aria-label={`Edit ${r.name}`}><EditIcon fontSize="small" /></IconButton>
                        <IconButton data-help="accounting.txnRules.action.delete" size="small" color="error" onClick={() => remove(r)} aria-label={`Delete ${r.name}`}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {rules.length === 0 && (
                  <TableRow><TableCell colSpan={canManage ? 8 : 7} align="center">No {SIDE_LABEL[side].toLowerCase()} mapping rules yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {editor && (
        <TxnMappingRuleEditor side={side} rule={editor.rule} kind={kind} accounts={accounts} segments={segments}
          onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} />
      )}
    </Box>
  );
};

export default TxnMappingRules;
